/**
 * Iter-165 — Category-specific post-submit funnel copy.
 *
 * Single source of truth for category-aware copy on the homepage form
 * (QuoteSuccessCard) + modal flow (IntakeSuccessCard). Hydrated from
 * the DB-backed funnel_copy registry on app boot via `hydrateFunnelCopy()`
 * — admins edit the copy through the Admin → Funnel Settings panel
 * without touching code.
 *
 * The in-code defaults below are the FALLBACK rendered before the API
 * round-trip completes (first paint / SSR / offline). The DB-backed copy
 * always wins once hydrated.
 *
 * Iter-165/168 product spec — replaced the prior alerts-promise surface
 * with a truthful "Save More with CarSavr (Optional)" newsletter capture
 * (we don't run rate-monitoring systems yet, so the old copy was a trust
 * violation). Trust microcopy added above the primary CTA.
 */
import { fetchFunnelCopy } from "./api";
import {
  CTA_INSURANCE,
  CTA_AUTO_LOAN,
  CTA_REFINANCE,
  CTA_WARRANTY,
} from "./ctaVocabulary";

const CATEGORIES = ["insurance", "auto-loan-new", "auto-loan-used", "refinance", "warranty"];

// In-code fallback registry. Lives here to keep the SSR/first-paint UX
// truthful before the API responds. Mirror of `_FUNNEL_COPY_DEFAULTS`
// in backend/server.py — keep these in sync so admin-side rollouts of
// new categories don't break the fallback.
//
// Iter-185.BJ — primary_cta_text fields now reference the central
// `ctaVocabulary` constants so accidental drift is impossible. The
// admin Funnel Copy panel still overrides these via MongoDB.
const FALLBACK_FUNNEL_COPY = {
  insurance: {
    success_headline: "Compare insurance rates available in ZIP {ZIP}.",
    success_description:
      "Review available insurance rates and compare options from trusted providers. Free to compare with no obligation.",
    email_capture_headline: "Save More with CarSavr (Optional)",
    email_capture_description:
      "Get occasional insurance savings tips, new tools, and ownership insights. No spam.",
    primary_cta_text: CTA_INSURANCE,
    secondary_cta_text: "Start Over",
  },
  "auto-loan-new": {
    success_headline: "Compare new-car financing offers available in ZIP {ZIP}.",
    success_description:
      "Review financing offers from trusted lenders and credit unions. Most comparisons take about 2 minutes and may help you secure a lower APR.",
    email_capture_headline: "Save More with CarSavr (Optional)",
    email_capture_description:
      "Get occasional financing tips, savings opportunities, and new CarSavr tools. No spam.",
    primary_cta_text: CTA_AUTO_LOAN,
    secondary_cta_text: "Start Over",
  },
  "auto-loan-used": {
    success_headline: "Compare used-car financing offers available in ZIP {ZIP}.",
    success_description:
      "Review available financing offers and compare rates before committing to dealer financing. Most comparisons take about 2 minutes.",
    email_capture_headline: "Save More with CarSavr (Optional)",
    email_capture_description:
      "Get occasional financing tips, savings opportunities, and new CarSavr tools. No spam.",
    primary_cta_text: CTA_AUTO_LOAN,
    secondary_cta_text: "Start Over",
  },
  refinance: {
    success_headline: "We found refinance options that may help reduce your APR and monthly payment.",
    success_description:
      "Compare refinance offers from trusted lending partners. Most drivers complete comparisons in less than 2 minutes.",
    email_capture_headline: "Save More with CarSavr (Optional)",
    email_capture_description:
      "Get occasional savings tips, new tools, and money-saving insights for vehicle owners. No spam.",
    primary_cta_text: CTA_REFINANCE,
    secondary_cta_text: "Start Over",
  },
  warranty: {
    success_headline: "Compare vehicle protection plans available for your vehicle.",
    success_description:
      "Compare warranty plans from trusted providers and review coverage options before making a decision.",
    email_capture_headline: "Save More with CarSavr (Optional)",
    email_capture_description:
      "Get occasional savings tips, new tools, and money-saving insights for vehicle owners. No spam.",
    primary_cta_text: CTA_WARRANTY,
    secondary_cta_text: "Start Over",
  },
};

// Default (category-less) fallback — guards against unknown category
// values from third-party intake flows.
const DEFAULT_COPY = {
  success_headline: "We found comparison options available in your area.",
  success_description:
    "Clicking Compare Options will route you to a vetted partner matching your inputs. Free to compare, no spam.",
  email_capture_headline: "Save More with CarSavr (Optional)",
  email_capture_description:
    "Get occasional savings tips, new tools, and money-saving insights for vehicle owners. No spam.",
  primary_cta_text: "Compare Options",
  secondary_cta_text: "Start Over",
};

// Module-level live registry. Starts as the in-code fallback and is
// replaced by the DB-backed row set when `hydrateFunnelCopy()` resolves.
let LIVE_FUNNEL_COPY = { ...FALLBACK_FUNNEL_COPY };

/** Replace LIVE_FUNNEL_COPY with the latest DB-backed rows. Safe to
 *  call repeatedly; swallows network errors (keeps fallback active). */
export async function hydrateFunnelCopy() {
  try {
    const items = await fetchFunnelCopy();
    if (!Array.isArray(items) || items.length === 0) return;
    const next = { ...FALLBACK_FUNNEL_COPY };
    for (const row of items) {
      if (row && row.category && CATEGORIES.includes(row.category)) {
        next[row.category] = {
          success_headline: row.success_headline || FALLBACK_FUNNEL_COPY[row.category].success_headline,
          success_description: row.success_description || FALLBACK_FUNNEL_COPY[row.category].success_description,
          email_capture_headline: row.email_capture_headline || FALLBACK_FUNNEL_COPY[row.category].email_capture_headline,
          email_capture_description: row.email_capture_description || FALLBACK_FUNNEL_COPY[row.category].email_capture_description,
          primary_cta_text: row.primary_cta_text || FALLBACK_FUNNEL_COPY[row.category].primary_cta_text,
          secondary_cta_text: row.secondary_cta_text || FALLBACK_FUNNEL_COPY[row.category].secondary_cta_text,
        };
      }
    }
    LIVE_FUNNEL_COPY = next;
  } catch {
    // Swallow — keep whatever's already in LIVE_FUNNEL_COPY.
  }
}

/** Return the truthful, category-specific copy block for the current
 *  funnel category. Unknown categories fall through to DEFAULT_COPY.
 *  `zip` is interpolated into any `{ZIP}` placeholder in the headline. */
export function getFunnelCopy(category, zip) {
  const base = LIVE_FUNNEL_COPY[category] || DEFAULT_COPY;
  const cleanZip = zip && zip !== "00000" ? String(zip) : "";
  return {
    success_headline: cleanZip
      ? base.success_headline.replace("{ZIP}", cleanZip)
      : base.success_headline.replace(/\s*in ZIP \{ZIP\}/g, "").replace("{ZIP}", "your area"),
    success_description: base.success_description,
    email_capture_headline: base.email_capture_headline,
    email_capture_description: base.email_capture_description,
    primary_cta_text: base.primary_cta_text,
    secondary_cta_text: base.secondary_cta_text,
  };
}

/**
 * Iter-145 legacy export — kept for callers that haven't migrated to
 * `getFunnelCopy` yet. Translates the iter-165 shape back into the
 * iter-145 {eyebrow, body, cta} contract.
 */
export function getCategoryCopy(category) {
  const cat = LIVE_FUNNEL_COPY[category] || DEFAULT_COPY;
  return {
    eyebrow: "Ready to compare",
    body: cat.success_description,
    cta: cat.primary_cta_text,
  };
}

/**
 * Per-category fallback page if the routing strategy is "lead-capture"
 * (no affiliate URL yet). Unchanged from iter-145.
 */
export const CATEGORY_COMPARE_DESTINATIONS = {
  insurance: "/auto-insurance",
  "auto-loan-new": "/auto-loans",
  "auto-loan-used": "/auto-loans",
  refinance: "/refinance",
  warranty: "/warranty",
};
